
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createTaskTitle } from "../redux/actions/tasksTitleActions";
import { FiPlus, FiCheck, FiArrowLeft, FiType, FiFileText, FiFlag, FiAlertCircle } from "react-icons/fi";
import { useNavigate } from "react-router-dom";

const CreateTaskTitle = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { creating, error, success } = useSelector((state) => state.tasksTitle);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    priority: "high"
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.description.trim()) return;

    dispatch(createTaskTitle(formData));
    
    // Reset form on success (we'll handle this in useEffect)
    if (!creating && !error) {
      setFormData({
        title: "",
        description: "",
        priority: "high"
      });
    }
  };

  // Reset form when creation is successful
  React.useEffect(() => {
    if (!creating && !error && success) {
      setFormData({
        title: "",
        description: "",
        priority: "high"
      });
    }
  }, [creating, error, success]);

  const isFormValid = formData.title.trim() !== "" && formData.description.trim() !== "";

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'text-red-600';
      case 'medium':
        return 'text-yellow-600';
      case 'low':
        return 'text-green-600';
      default:
        return 'text-gray-600';
    }
  };

  if (creating) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400 text-lg">Creating Task Title...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate("/dashboard/task-title")}
            className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <FiArrowLeft className="text-lg" />
            Back to Task Titles
          </button>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          {/* Form Header */}
          <div className="bg-gradient-to-r from-blue-500 to-cyan-500 px-6 py-8">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-xl">
                <FiPlus className="text-2xl text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white mb-2">
                  Create Task Title
                </h1>
                <p className="text-blue-100">
                  Add a new task title with priority and description
                </p>
              </div>
            </div>
          </div>

          {/* Form Content */}
          <div className="p-6">
            {/* Success Message */}
            {success && (
              <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                    <FiCheck className="text-white text-sm" />
                  </div>
                  <div>
                    <p className="text-green-800 dark:text-green-300 font-medium">
                      Task title created successfully!
                    </p>
                    <p className="text-green-600 dark:text-green-400 text-sm">
                      The new task title is now available for use.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center">
                    <FiAlertCircle className="text-white text-sm" />
                  </div>
                  <div>
                    <p className="text-red-800 dark:text-red-300 font-medium">
                      {typeof error === "string" ? error : error?.message || "Something went wrong"}
                    </p>
                    <p className="text-red-600 dark:text-red-400 text-sm mt-1">
                      Please check your input and try again.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 gap-6">
                {/* Title */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    <div className="flex items-center gap-2">
                      <FiType className="text-sm" />
                      Task Title <span className="text-red-500">*</span>
                    </div>
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => handleChange("title", e.target.value)}
                    placeholder="Enter task title (e.g., Client Meeting, Site Inspection, Documentation, etc.)"
                    className="w-full bg-white dark:bg-gray-700 border-0 rounded-xl py-3 px-4 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 transition placeholder-gray-500 dark:placeholder-gray-400"
                    required
                    disabled={creating}
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    This will be the main identifier for the task
                  </p>
                </div>

                {/* Priority */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    <div className="flex items-center gap-2">
                      <FiFlag className="text-sm" />
                      Priority <span className="text-red-500">*</span>
                    </div>
                  </label>
                  <div className="grid grid-cols-3 gap-3">
                    {['high', 'medium', 'low'].map((level) => (
                      <button
                        key={level}
                        type="button"
                        onClick={() => handleChange("priority", level)}
                        className={`p-3 rounded-xl border-2 transition-all ${
                          formData.priority === level
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                            : 'border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-500'
                        }`}
                      >
                        <div className="flex flex-col items-center gap-1">
                          <FiFlag className={`text-lg ${getPriorityColor(level)}`} />
                          <span className="text-sm font-medium capitalize">{level}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Select the urgency level for this type of task
                  </p>
                </div>

                {/* Description */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    <div className="flex items-center gap-2">
                      <FiFileText className="text-sm" />
                      Description <span className="text-red-500">*</span>
                    </div>
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => handleChange("description", e.target.value)}
                    placeholder="Enter detailed description of what this task entails, requirements, and any specific instructions..."
                    rows={4}
                    className="w-full bg-white dark:bg-gray-700 border-0 rounded-xl py-3 px-4 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 transition placeholder-gray-500 dark:placeholder-gray-400 resize-none"
                    required
                    disabled={creating}
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Provide clear instructions and context for this task type
                  </p>
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => {
                    setFormData({
                      title: "",
                      description: "",
                      priority: "high"
                    });
                  }}
                  disabled={creating}
                  className="flex items-center justify-center gap-2 bg-gray-500 hover:bg-gray-600 text-white py-3 px-6 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Clear Form
                </button>
                
                <button
                  type="submit"
                  disabled={creating || !isFormValid}
                  className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white py-3 px-6 rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
                >
                  {creating ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Creating Task Title...
                    </>
                  ) : (
                    <>
                      <FiCheck className="text-lg" />
                      Create Task Title
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Form Tips */}
        <div className="mt-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-2xl p-6">
          <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-3">
            💡 Task Title Guidelines
          </h3>
          <ul className="text-blue-800 dark:text-blue-200 text-sm space-y-2">
            <li>• Use clear and descriptive titles that employees will easily understand</li>
            <li>• Set appropriate priority levels based on task urgency and importance</li>
            <li>• High: Critical tasks requiring immediate attention</li>
            <li>• Medium: Important tasks with flexible deadlines</li>
            <li>• Low: Routine tasks without strict deadlines</li>
            <li>• Provide detailed descriptions to ensure task clarity</li>
            <li>• Fields marked with * are required</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default CreateTaskTitle;



